import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  SystemConfig,
  SystemConfigBlobGuestService,
  SystemConfigGuestService,
} from '@gk/gk-modules';
import { map, Observable, takeWhile } from 'rxjs';
import { PortalTranslationDropdownItemId } from '../../../components/navbar-provider/dynamic-navbar.configuration';
import { WindowRefService } from '../../../services/window-ref/window-ref.service';
import { ModalDocumentVerificationComponent } from '../departments/geodesy/modal-document-verification/modal-document-verification.component';
import { ModalRequestsListComponent } from '../departments/geodesy/modal-requests-list/modal-requests-list.component';

@Component({
  selector: 'app-department-footer',
  templateUrl: './department-footer.component.html',
  styleUrl: './department-footer.component.scss',
  standalone: false,
})
export class DepartmentFooterComponent implements OnInit, OnDestroy {
  private isAlive = true;
  @ViewChild(ModalRequestsListComponent)
  modalRequestsListComponent: ModalRequestsListComponent;
  @ViewChild(ModalDocumentVerificationComponent)
  modalDocumentVerificationComponent: ModalDocumentVerificationComponent;
  translationNavItemId: string;
  @Input() isRequestsButtonVisible = true;
  @Input() isDocumentVerificationButtonVisible = true;
  @Input() isAccessibilityStatementButtonVisible = true;

  constructor(
    private route: ActivatedRoute,
    private windowRefService: WindowRefService,
    public systemConfigBlobGuestService: SystemConfigBlobGuestService,
    private systemConfigGuestService: SystemConfigGuestService,
  ) {}

  ngOnInit(): void {
    this.subscribeToRouteParams();
  }

  subscribeToRouteParams(): void {
    this.route.queryParams
      .pipe(takeWhile(() => this.isAlive))
      .subscribe((params) => {
        if (params['showDocumentVerificationWindow']) {
          setTimeout(() => this.showModalDocumentVerification());
        }
      });
  }

  goToInstructions(): void {
    this.windowRefService.nativeWindow.location.href = `${window.location.origin}/dokuweb/`;
  }

  goToFaq(): void {
    this.windowRefService.nativeWindow.location.href = `${window.location.origin}/dokuweb/faq/faq.html`;
  }

  showModalRequestsList(): void {
    this.modalRequestsListComponent.showModal();
  }

  showModalDocumentVerification(): void {
    this.modalDocumentVerificationComponent.showModal();
  }

  getTeamViewerDownloadUrl(): string {
    return `https://www.geomatyka-krakow.pl/download/TeamViewerQS-idc6s8u3r3.exe`;
  }

  maybeSetTranslationNavItemId(navItemId: string): void {
    if (
      Object.values(PortalTranslationDropdownItemId).includes(
        navItemId as PortalTranslationDropdownItemId,
      )
    ) {
      this.translationNavItemId = navItemId;
    }
  }

  getTranslateKeyWithAddedLanguageSuffix(
    translateKeyBase: string,
    forceForPl?: string,
  ): string {
    switch (this.translationNavItemId) {
      case PortalTranslationDropdownItemId.PolishTranslation:
        return forceForPl || `${translateKeyBase}`;
      case PortalTranslationDropdownItemId.EnglishTranslation:
        return `${translateKeyBase}_ENGLISH`;
      case PortalTranslationDropdownItemId.GermanTranslation:
        return `${translateKeyBase}_GERMAN`;
      case PortalTranslationDropdownItemId.CzechTranslation:
        return `${translateKeyBase}_CZECH`;
      default:
        return forceForPl || `${translateKeyBase}`;
    }
  }

  getTranslateUsefulInfoNavInstructionKey(): Observable<string> {
    const usefulNavButtonInstrutionBase =
      'OFFICE_DEPARTMENTS_PORTAL.GEODESY.USEFUL_INFO_NAV.BUTTONS.INSTRUCTIONS';

    return this.systemConfigGuestService
      .getSysConfigGuestValueByKeyCached(
        SystemConfig.WeParametersFooterInstructionName,
      )
      .pipe(
        map((instructionLabel) => {
          return this.getTranslateKeyWithAddedLanguageSuffix(
            usefulNavButtonInstrutionBase,
            instructionLabel,
          );
        }),
      );
  }

  getTranslateUsefulInfoNavRequestsKey(): string {
    const usefulNavButtonRequestsBase =
      'OFFICE_DEPARTMENTS_PORTAL.GEODESY.USEFUL_INFO_NAV.BUTTONS.REQUESTS';
    return this.getTranslateKeyWithAddedLanguageSuffix(
      usefulNavButtonRequestsBase,
    );
  }

  getTranslateUsefulInfoNavDocVerificationKey(): string {
    const usefulNavButtonDocVerificationBase =
      'OFFICE_DEPARTMENTS_PORTAL.GEODESY.USEFUL_INFO_NAV.BUTTONS.DOCUMENT_VERIFICATION';
    return this.getTranslateKeyWithAddedLanguageSuffix(
      usefulNavButtonDocVerificationBase,
    );
  }

  getTranslateUsefulInfoNavRemoteHelpKey(): string {
    const usefulNavButtonRemoteHelpBase =
      'OFFICE_DEPARTMENTS_PORTAL.GEODESY.USEFUL_INFO_NAV.BUTTONS.REMOTE_HELP';
    return this.getTranslateKeyWithAddedLanguageSuffix(
      usefulNavButtonRemoteHelpBase,
    );
  }

  getTranslateUsefulInfoNavAccessibilityStatementKey(): string {
    const usefulNavButtonAccessibilityStatementBase = 'ACCESSIBILITY_STATEMENT';
    return this.getTranslateKeyWithAddedLanguageSuffix(
      usefulNavButtonAccessibilityStatementBase,
    );
  }

  ngOnDestroy(): void {
    this.isAlive = false;
  }
}
